import { z } from 'zod'

enum Gender {
  Man = 'man',
  Woman = 'woman',
  Different = 'different',
}
const validatePostcodeRegex = /^[1-9][0-9]{3} ?(?!sa|sd|ss)[a-z]{2}$/i
const validateHouseNumberRegex = /^[1-9]\d*(?: ?(?:[/-] ?\d+?))?$/dm
const validatePhoneNumberRegex = /^\d{10}$/

export const buildSchemaFromValidation = (validation: Record<string, any>, customControlType?: string, inputType?: string) => {
  // If there's no validation, return optional any
  if (!validation || !Object.keys(validation).length) return z.any().optional()
  
  // Handle checkbox fields and switch components - they return boolean values, not strings
  if (inputType === 'checkbox' || customControlType === 'switch') {
    if (validation.required) {
      return z.boolean().refine(val => val !=undefined, {
        message: 'required',
      })
    }
    return z.boolean().optional()
  }
  
  // Handle radioGroupGender components
  if (customControlType === 'radioGroupGender') {
    if (validation.required) {
      return z.string({
        required_error: 'required',
        invalid_type_error: 'required',
      }).min(1, 'required');
    }
    return z.string().nullable().optional();
  }
  
  // Handle radio group fields that return array values
  if (customControlType === 'radioGroup') {
    if (validation.required) {
      return z.array(z.string()).min(1, 'required')
    }
    return z.array(z.string()).optional()
  }

  // Handle picture-choice fields that return string values
  if (customControlType === 'picture-choice') {
    if (validation.required) {
      return z.string().min(1, 'required')
    }
    return z.string().optional()
  }

  // Handle shadcn-select fields that return string values
  if (customControlType === 'shadcn-select') {
    if (validation.required) {
      return z.string().min(1, 'required')
    }
    return z.string().optional()
  }

  // Handle email validation first as it changes the schema type
  if (customControlType === 'email') {
    if (validation.required) {
      // Required email - must be valid format
      let emailSchema = z.string().email({ message: 'invalid_email' })
      
      return emailSchema
    } else {
      // Optional email - either empty string or valid email
      return z.union([
        z.literal(''),
        z.string().email({ message: 'invalid_email' })
      ])
    }
  }
  
  // Regular string validation
  // If the field is not required, we need a different approach to handle min/max length
  if (!validation.required) {
    // For non-required fields, we'll use a different approach that accepts empty strings
    // but validates against min/max length when a value is provided
    
    // Create a union of an empty string with a string that meets our min/max criteria
    let validatedString = z.string();
    
    if (validation.minLength) {
      const minLength = Number(validation.minLength);
      validatedString = validatedString.min(minLength, 'min_length');
    }
    
    if (validation.maxLength) {
      const maxLength = Number(validation.maxLength);
      validatedString = validatedString.max(maxLength, 'max_length');
    }
    
    // Allow either an empty string OR a string that meets our validations
    return z.union([
      z.literal(''),  // Allow empty string
      validatedString // Or a string that meets our validation rules
    ]).optional();
  }
  
  // For required fields, use the standard approach
  let schema = z.string().min(1, 'required');
  
  if (validation.minLength) {
    const minLength = Number(validation.minLength);
    // Make sure minLength is at least 1 to avoid conflicts with the required validation
    if (minLength > 1) {
      schema = schema.min(minLength, 'min_length');
    }
  }
  
  if (validation.maxLength) { 
    schema = schema.max(Number(validation.maxLength), 'max_length');
  }
  
  return schema
}

export const conditionalField = (isRequired: boolean) =>
  isRequired
    ? z
        .string({
          errorMap: () => {
            return { message: 'required' }
          },
        })
        .min(1, 'required')
    : z.any().optional().nullable()

export const validateGender = (isRequired: boolean) =>
  isRequired
    ? z.nativeEnum(Gender, {
        errorMap: () => {
          return { message: 'required' }
        },
      })
    : z.nativeEnum(Gender).optional().nullable()

export const validatePostcode = (isRequired: boolean) =>
  conditionalField(isRequired).and(
    z
      .string()
      .optional()
      .refine(
        (value) =>
          value && value?.length > 0 ? validatePostcodeRegex.test(value) : true,
        'invalid_postcode',
      ),
  )

export const validateHouseNumber = (isRequired: boolean) =>
  conditionalField(isRequired).and(
    z
      .string()
      .optional()
      .refine(
        (value) =>
          value && value?.length > 0
            ? validateHouseNumberRegex.test(value)
            : true,
        'invalid_house_number',
      ),
  )

const optionalEmailInput = (schema: z.ZodString) =>
  z
    .union([z.string(), z.undefined()])
    .refine((val) => !val || schema.safeParse(val).success, {
      message: 'invalid_email',
    })

export const validateEmail = (isRequired: boolean) =>
  isRequired
    ? conditionalField(isRequired).and(
        z.string().email({ message: 'invalid_email' }),
      )
    : optionalEmailInput(z.string().email())

export const validatePhoneNumber = (isRequired: boolean) =>
  conditionalField(isRequired).and(
    z
      .string()
      .optional()
      .nullable()
      .refine(
        (value) =>
          value && value?.length > 0
            ? validatePhoneNumberRegex.test(value)
            : true,
        'invalid_phone_number',
      ),
  )

